import { Navigate, useLocation } from 'react-router-dom'
import { Spinner } from 'react-bootstrap'
import { useAuth } from '../context/AuthContext'
import { useMsal } from '@azure/msal-react'

const SSO_WAIT_MESSAGE =
  'Please wait whilst we check your credentials with Microsoft.'

interface ProtectedRouteProps {
  children: React.ReactNode
}

/**
 * Redirects to /login if not authenticated. Used to wrap routes that require auth.
 */
export default function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated } = useAuth()
  const location = useLocation()
  const { accounts } = useMsal()

  if (!isAuthenticated) {
    const hash = location.hash || ''
    const isEntraRedirectResponse =
      hash.includes('code=') || hash.includes('id_token=') || hash.includes('access_token=')

    // During the Entra redirect flow, MSAL can already have an account cached, but our AuthContext
    // token may not have been bridged yet. Avoid redirect-looping to /login in that case.
    if (isEntraRedirectResponse || accounts.length > 0) {
      return (
        <div
          style={{
            minHeight: '100vh',
            background: '#f3f4f6',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '2rem',
            textAlign: 'center',
          }}
        >
          <div style={{ maxWidth: 420 }}>
            <Spinner animation="border" className="mb-3" role="status" aria-hidden />
            <p className="mb-2 fw-medium">{SSO_WAIT_MESSAGE}</p>
            <p className="text-muted small mb-0">
              This usually takes a few seconds. Do not close this page or click Sign in again.
            </p>
          </div>
        </div>
      )
    }
    return <Navigate to="/login" state={{ from: location }} replace />
  }
  return <>{children}</>
}
